/** Timber trestle carrying an open flume; hook_0 touches the painted outfall lip. */
import { checkEnvelope, checkScope, el, partGroup } from '../../svg';
import type { PieceArt, PieceRecipe, ResolvedPalette } from '../../contract';
import { amount, beam, flag, grainRng, ground, line, rivet } from './machinerySvg';
export interface FlumeTrestleParams { readonly legs: number; readonly brace: boolean }
export type FlumeTrestleRecipe = PieceRecipe<'mill.flume_trestle.v1', FlumeTrestleParams>;
export const FLUME_TRESTLE_FRAME = { w: 160, h: 180 } as const;
export const MILL_FLUME_TRESTLE: FlumeTrestleRecipe = {
  schema: 'hush.asset-recipe.v1', id: 'mill.flume_trestle.default.v1', generatorId: 'mill.flume_trestle.v1',
  seed: 20071003, paletteId: 'millrace_amber', params: { legs: 3, brace: true },
};
export function buildFlumeTrestle(recipe: FlumeTrestleRecipe, p: ResolvedPalette, scope: string): PieceArt {
  checkEnvelope(recipe, MILL_FLUME_TRESTLE.generatorId); checkScope(scope);
  const legs = amount(recipe.params.legs, 'legs', 2, 3, true), brace = flag(recipe.params.brace, 'brace');
  const random = grainRng(recipe), parts: string[] = [];
  let piers = ground(160, 180, p), framing = '';
  for (let i = 0; i < legs; i++) {
    const x = 28 + i * 104 / (legs - 1), topX = x + (80 - x) * 0.13;
    piers += el('path', { d: `M${x - 12} 176L${x - 10} 162L${x + 10} 160L${x + 13} 176Z`, fill: p.stoneBase, stroke: p.outline, 'stroke-width': 1.2 });
    framing += beam(x, 165, topX, 35, 11, p) + rivet(topX, 42, p);
    framing += line(x + 1, 150, topX + 2 + random(`split.${i}`) * 2, 57, p.woodShadow, 1);
  }
  if (brace) framing += beam(31, 145, 128, 57, 7, p) + beam(32, 56, 130, 145, 7, p);
  framing += beam(29, 58, 131, 58, 9, p) + beam(29, 147, 132, 147, 8, p);
  const trough = el('path', { d: 'M5 14L144 14L154 22L15 22Z', fill: p.woodShadow, stroke: p.outline, 'stroke-width': 1.4 })
    + el('path', { d: 'M12 17L143 17L149 21L17 21Z', fill: p.waterFar })
    + line(24, 19, 141, 19, p.waterLight, 1)
    + el('path', { d: 'M6 23L154 23L154 43L6 43Z', fill: p.woodBase, stroke: p.outline, 'stroke-width': 1.5 })
    + line(7, 25, 152, 25, p.woodLight, 2)
    + line(17, 34, 140, 33 + random('trough.grain') * 3, p.woodShadow, 1)
    + el('path', { d: 'M25 22H32V44H25ZM125 22H132V44H125Z', fill: p.paperShadow, stroke: p.outline, 'stroke-width': 0.8 })
    + rivet(28, 32, p, 1.6) + rivet(128, 32, p, 1.6);
  const body = partGroup(parts, 'trestle-footings', piers) + partGroup(parts, 'trestle-joinery', framing)
    + partGroup(parts, 'trestle-trough', trough);
  return { frame: FLUME_TRESTLE_FRAME, body, anchors: { support: { x: 80, y: 180 }, hook_0: { x: 153, y: 24 } }, parts, openings: [] };
}
